import { useCallback, useEffect, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useAuth } from '../../auth/AuthContext';
import SaktiAccessCodeApiService from '../services/saktiAccessCodeApiService';
import { getTodayInJakartaForApi } from '../utils/date';
import {
  SaktiAccessCodeFilters,
  SaktiAccessCodeRequest,
} from '../types';

interface UseSaktiAccessCodeResult {
  requests: SaktiAccessCodeRequest[];
  total: number;
  loading: boolean;
  draftFilters: SaktiAccessCodeFilters;
  appliedFilters: SaktiAccessCodeFilters;
  setFilters: (filters: SaktiAccessCodeFilters) => void;
  applyFilters: () => void;
  fetchRequests: () => Promise<void>;
  viewCode: (id: string) => Promise<string | null>;
  cancelRequest: (id: string) => Promise<boolean>;
}

export const useSaktiAccessCode = (): UseSaktiAccessCodeResult => {
  const { user } = useAuth();
  const toast = useToast();
  const adminId = user?.email || 'admin';
  const today = getTodayInJakartaForApi();

  const [requests, setRequests] = useState<SaktiAccessCodeRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [draftFilters, setDraftFiltersState] = useState<SaktiAccessCodeFilters>({
    limit: 20,
    offset: 0,
    status: 'PENDING',
    date_from: today,
    date_to: today,
  });
  const [appliedFilters, setAppliedFilters] = useState<SaktiAccessCodeFilters>({
    limit: 20,
    offset: 0,
    status: 'PENDING',
    date_from: today,
    date_to: today,
  });

  const setFilters = useCallback((nextFilters: SaktiAccessCodeFilters) => {
    setDraftFiltersState((prev) => ({ ...prev, ...nextFilters }));
  }, []);

  const applyFilters = useCallback(() => {
    setAppliedFilters(draftFilters);
  }, [draftFilters]);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await SaktiAccessCodeApiService.getRequests(appliedFilters, adminId);
      if (response.success) {
        setRequests(response.data);
        setTotal(response.total);
      }
    } catch (error) {
      toast({
        title: 'Gagal memuat data',
        description: error instanceof Error ? error.message : 'Terjadi kesalahan',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, adminId, toast]);

  // Countdown timer for expires in
  useEffect(() => {
    const timer = setInterval(() => {
      setRequests((prevRequests) =>
        prevRequests.map((req) => {
          if (req.status === 'PENDING' && req.seconds_remaining > 0) {
            return {
              ...req,
              seconds_remaining: req.seconds_remaining - 1,
            };
          }
          return req;
        })
      );
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const viewCode = useCallback(async (id: string) => {
    try {
      const response = await SaktiAccessCodeApiService.getPlaintextCode(id, adminId);
      if (response.success) {
        return response.data.code;
      }
      return null;
    } catch (error) {
      toast({
        title: 'Gagal mengambil kode',
        description: error instanceof Error ? error.message : 'Kode tidak tersedia atau expired',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return null;
    }
  }, [adminId, toast]);

  const cancelRequest = useCallback(async (id: string) => {
    try {
      const response = await SaktiAccessCodeApiService.cancelRequest(id, adminId);
      if (response.success) {
        toast({
          title: 'Berhasil',
          description: response.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        await fetchRequests();
        return true;
      }
      return false;
    } catch (error) {
      toast({
        title: 'Gagal membatalkan',
        description: error instanceof Error ? error.message : 'Terjadi kesalahan',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
  }, [adminId, fetchRequests, toast]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  return {
    requests,
    total,
    loading,
    draftFilters,
    appliedFilters,
    setFilters,
    applyFilters,
    fetchRequests,
    viewCode,
    cancelRequest,
  };
};

export default useSaktiAccessCode;
